import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Lock, FileCheck, AlertTriangle, TrendingUp, ArrowLeft, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

const PLAN_LABEL = { DOCUMENTA: "DOCUMENTA", REGISTRA: "REGISTRA", TRAZA: "TRAZA" };

function recomendar(resumen) {
  if (!resumen) return "REGISTRA";
  if (resumen.traza_usado || resumen.readiness > 60) return "TRAZA";
  if (resumen.readiness >= 30) return "REGISTRA";
  return "DOCUMENTA";
}

export default function Activar() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [planes, setPlanes] = useState([]);
  const [resumen, setResumen] = useState(null);
  const expirado = tenant?.estado === "expirado";
  const recomendado = recomendar(resumen);

  useEffect(() => {
    api.get("/planes").then((r) => setPlanes(r.data)).catch(() => {});
    api.get("/cuenta/resumen").then((r) => setResumen(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="activar-page" className="max-w-6xl mx-auto">
      <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-[#0E2A47]/60 hover:text-[#FF5625] mb-4" data-testid="activar-volver"><ArrowLeft size={15} />Volver al panel</button>

      <div className="text-center mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5625] mb-2">{expirado ? "Tu prueba ha terminado" : "Activa tu plan"}</div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0E2A47]">
          {expirado ? "Todo tu trabajo sigue aquí" : "Elige tu plan y sigue avanzando"}
        </h1>
        <p className="text-[#0E2A47]/60 mt-2 max-w-2xl mx-auto">
          {expirado
            ? "Tu cuenta está en modo solo lectura. Activa un plan para volver a editar. No hemos borrado nada."
            : "Cuando quieras, activa un plan para asegurar el acceso completo cuando termine tu prueba."}
        </p>
      </div>

      {resumen && (
        <div className="max-w-2xl mx-auto mb-8" data-testid="activar-readiness">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Tu readiness</span>
            <span className="text-lg font-black text-[#0E2A47]" data-testid="activar-readiness-valor">{resumen.readiness}%</span>
          </div>
          <Progress value={resumen.readiness} className="h-3" />
          <p className="text-sm text-[#0E2A47]/70 mt-3 text-center" data-testid="activar-readiness-texto">
            Tu readiness: <b>{resumen.readiness}%</b> · <b>{resumen.gaps_abiertos}</b> gaps abiertos — el trabajo que llevas hecho sigue creciendo con tu plan
          </p>
          <div className="mt-4 rounded-lg bg-[#FFF3EF] border border-[#FF5625]/20 px-4 py-3 flex items-center gap-2 justify-center text-sm" data-testid="activar-recomendacion">
            <Sparkles size={16} className="text-[#FF5625] shrink-0" />
            <span className="text-[#0E2A47]">Para tu momento, te recomendamos <b className="text-[#FF5625]">{PLAN_LABEL[recomendado]}</b>.</span>
          </div>
        </div>
      )}

      {resumen && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="activar-resumen">
          <Resumen icon={FileCheck} label="Tus documentos" value={resumen.documentos} />
          <Resumen icon={TrendingUp} label="Tu cobertura" value={`${resumen.cobertura}%`} accent />
          <Resumen icon={AlertTriangle} label="Gaps abiertos" value={resumen.gaps_abiertos} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {planes.map((p) => (
          <Card key={p.codigo} className={`p-6 border-[#E2E8F0] flex flex-col ${p.codigo === recomendado ? "ring-2 ring-[#FF5625]" : ""}`} data-testid={`plan-${p.codigo}`}>
            {p.codigo === recomendado && <Badge className="bg-[#FF5625] hover:bg-[#FF5625] w-fit mb-2">Recomendado para ti</Badge>}
            <div className="font-black text-xl tracking-tighter text-[#0E2A47]">{p.nombre}</div>
            <div className="text-sm text-[#0E2A47]/60 mt-1">{p.descripcion}</div>
            <div className="mt-4 flex items-end gap-1">
              <span className="text-4xl font-black tracking-tighter text-[#0E2A47]">{p.precio}{p.moneda}</span>
              <span className="text-sm text-[#0E2A47]/50 mb-1">/{p.periodo}</span>
            </div>
            <ul className="mt-4 space-y-2 flex-1">
              {p.features.map((ft, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#0E2A47]/80"><Check size={16} className="text-[#10B981] mt-0.5 shrink-0" />{ft}</li>
              ))}
            </ul>
            {p.disponible ? (
              <Button asChild className="mt-6 bg-[#0E2A47] hover:bg-[#163C65]" data-testid={`plan-pagar-${p.codigo}`}>
                <a href={p.pay_link} target="_blank" rel="noreferrer">Contratar {p.nombre}</a>
              </Button>
            ) : (
              <Button disabled className="mt-6" data-testid={`plan-proximamente-${p.codigo}`}><Lock size={14} className="mr-1" />Próximamente</Button>
            )}
          </Card>
        ))}
      </div>

      <p className="text-center text-xs text-[#0E2A47]/40 mt-8">
        Tras el pago, tu cuenta se activa en cuanto lo confirmamos. Tus datos se conservan un mínimo de 90 días tras expirar la prueba.
      </p>
    </div>
  );
}

function Resumen({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 border-[#E2E8F0] text-center">
      <Icon size={20} className={`mx-auto mb-2 ${accent ? "text-[#FF5625]" : "text-[#0E2A47]/40"}`} />
      <div className="text-3xl font-black tracking-tighter text-[#0E2A47]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#0E2A47]/50 mt-1">{label}</div>
    </Card>
  );
}
